/**
 * Catalogo de modulos, planos e entitlements.
 *
 * Regra do projeto: nunca escreva `if (plano === 'pro')` no codigo de feature.
 * Pergunte sempre ao entitlement (`can('packages.qr_pickup')` /
 * `limitOf('residents.max')`). Assim um plano novo, ou uma excecao comercial
 * para um condominio especifico, e so linha em banco — nao e deploy.
 */
// ---------------------------------------------------------------------------
// Modulos do produto
// ---------------------------------------------------------------------------
/** Cada modulo e uma area do sistema que pode ser vendida separadamente. */
export const MODULE_CODES = [
    'core', // sempre presente: moradores, unidades, usuarios, auditoria
    'packages', // encomendas  <- primeiro modulo a ser entregue
    'visitors', // portaria / visitantes
    'reservations', // reserva de areas comuns
    'maintenance', // chamados e manutencao
    'financial', // boletos, inadimplencia
    'communications', // avisos, mural, enquetes
    'assemblies', // assembleias e votacao
];
export const MODULE_LABELS = {
    core: 'Base',
    packages: 'Encomendas',
    visitors: 'Portaria e visitantes',
    reservations: 'Reservas',
    maintenance: 'Manutencao',
    financial: 'Financeiro',
    communications: 'Comunicacao',
    assemblies: 'Assembleias',
};
// ---------------------------------------------------------------------------
// Entitlements
// ---------------------------------------------------------------------------
/** Limites numericos. Valor `null` = ilimitado. */
export const LIMIT_KEYS = [
    'core.units.max',
    'core.residents.max',
    'core.users.max',
    'packages.per_month',
    'packages.retention_days',
    'storage.mb',
];
/** Flags booleanas de funcionalidade. */
export const FEATURE_KEYS = [
    'core.audit_log',
    'core.api_access',
    'core.custom_branding',
    'core.multi_condominium',
    'packages.whatsapp_notifications',
    'packages.qr_pickup',
    'packages.photo_capture',
    'packages.bulk_import',
    'packages.authorized_pickup',
    'packages.reports',
];
/**
 * Nomes humanos das chaves de entitlement.
 *
 * Vivem aqui, e nao na tela que os desenhou primeiro, porque agora sao DUAS as
 * telas que precisam concordar sobre o que `packages.per_month` quer dizer: o
 * catalogo de planos e a excecao comercial por conta. Uma copia em cada uma
 * divergiria no primeiro rotulo reescrito, e o backoffice passaria a chamar a
 * mesma chave de dois nomes em telas vizinhas.
 */
export const LIMIT_LABELS = {
    'core.units.max': 'Unidades',
    'core.residents.max': 'Moradores',
    'core.users.max': 'Contas de acesso',
    'packages.per_month': 'Encomendas por mes',
    'packages.retention_days': 'Retencao (dias)',
    'storage.mb': 'Armazenamento (MB)',
};
export const FEATURE_LABELS = {
    'core.audit_log': 'Historico de auditoria',
    'core.api_access': 'Acesso por API',
    'core.custom_branding': 'Marca propria',
    'core.multi_condominium': 'Multiplos condominios',
    'packages.whatsapp_notifications': 'Aviso por WhatsApp',
    'packages.qr_pickup': 'Retirada por QR',
    'packages.photo_capture': 'Foto da encomenda',
    'packages.bulk_import': 'Importacao por planilha',
    'packages.authorized_pickup': 'Retirada autorizada',
    'packages.reports': 'Relatorios',
};
/** A chave e um limite numerico? O contrario e uma feature booleana. */
export function isLimitKey(key) {
    return LIMIT_KEYS.includes(key);
}
export function isFeatureKey(key) {
    return FEATURE_KEYS.includes(key);
}
export function entitlementLabel(key) {
    return isLimitKey(key) ? LIMIT_LABELS[key] : FEATURE_LABELS[key];
}
// ---------------------------------------------------------------------------
// Assinatura
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'canceled',
    'suspended',
];
/** Status que ainda permitem usar o sistema (past_due = periodo de graca). */
export const ACTIVE_SUBSCRIPTION_STATUSES = [
    'trialing',
    'active',
    'past_due',
];
// ---------------------------------------------------------------------------
// Helpers de leitura (usados no front e no back)
// ---------------------------------------------------------------------------
export function hasModule(ent, module) {
    return module === 'core' || ent.modules.includes(module);
}
export function canUse(ent, feature) {
    return ent.features[feature] === true;
}
/** `null` = ilimitado, `0` = nao contratado. */
export function limitOf(ent, key) {
    const value = ent.limits[key];
    return value === undefined ? 0 : value;
}
export function usageOf(ent, key) {
    return ent.usage[key] ?? 0;
}
/** True quando o consumo ja bateu o limite do periodo. */
export function isOverLimit(ent, key) {
    const limit = limitOf(ent, key);
    if (limit === null)
        return false;
    return usageOf(ent, key) >= limit;
}
/**
 * Serializacao dos entitlements no banco: uma coluna VARCHAR guarda
 * `'true' | 'false' | '250' | 'unlimited'`. Manter o parse num lugar so evita
 * divergencia entre API, seed e painel administrativo.
 */
export function parseEntitlementValue(raw) {
    if (raw === 'true')
        return true;
    if (raw === 'false')
        return false;
    if (raw === 'unlimited')
        return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : false;
}
// ---------------------------------------------------------------------------
// Troca de plano
// ---------------------------------------------------------------------------
/**
 * O sindico SOLICITA, a Azko decide. Nunca autoaplicavel.
 *
 * Nao e burocracia: mudar de plano muda o que a conta pode fazer e quanto ela
 * paga. Upgrade autoaplicado significaria cobrar mais sem ninguem confirmar, e
 * downgrade autoaplicado cortaria modulo em uso no meio do expediente da
 * portaria. Por isso e uma tabela de pedido, e nao um UPDATE em `subscriptions`.
 */
export const PLAN_CHANGE_STATUSES = [
    'pending',
    'approved',
    'rejected',
    'canceled',
];
export const PLAN_CHANGE_STATUS_LABELS = {
    pending: 'Aguardando a Azko',
    approved: 'Aprovada',
    rejected: 'Recusada',
    canceled: 'Cancelada pelo condominio',
};
/**
 * Para onde a conta esta indo. Sai da comparacao de preco, e nao da ordem dos
 * planos no catalogo, porque plano fechado/legado nao entra nessa ordem.
 */
export const PLAN_CHANGE_DIRECTIONS = ['upgrade', 'downgrade', 'lateral'];
export const PLAN_CHANGE_DIRECTION_LABELS = {
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    lateral: 'Mesmo preco',
};
export const USAGE_FILTERS = ['all', 'near', 'over', 'idle'];
export const USAGE_FILTER_LABELS = {
    all: 'Todas',
    near: 'Perto do teto',
    over: 'Estourou',
    idle: 'Parada',
};
export const ACCOUNT_ROLE_LABELS = {
    owner: 'Dono da conta',
    manager: 'Sindico',
    concierge: 'Porteiro',
    viewer: 'Leitura',
};
export const SUBSCRIPTION_STATUS_LABELS = {
    trialing: 'em teste',
    active: 'ativa',
    past_due: 'pagamento atrasado',
    canceled: 'cancelada',
    suspended: 'suspensa',
};
/**
 * Nomes humanos para as acoes do `platform_audit_log`.
 *
 * O log guarda `assinante.acesso_cortado` porque e uma chave estavel, boa para
 * filtrar e imune a mudanca de texto. Mas quem le a tela e uma pessoa do
 * suporte no meio de um atendimento — mostrar a chave crua obriga a traduzir de
 * cabeca justamente na hora em que ela esta com o cliente no telefone.
 *
 * Acao sem traducao cai no proprio codigo, entao um registro novo nunca some
 * da tela por esquecimento.
 */
export const PLATFORM_ACTION_LABELS = {
    'assinante.acesso_cortado': 'Acesso cortado',
    'assinante.acesso_liberado': 'Acesso liberado',
    // As cinco abaixo ja eram gravadas e nenhuma tinha traducao: apareciam como
    // chave crua no historico da conta, que e a tela que o suporte le com o
    // cliente no telefone. O fallback funcionou como prometido — nada sumiu —,
    // mas "assinante.credenciais_reenviadas" no meio de um atendimento e
    // exatamente a traducao de cabeca que este mapa existe para evitar.
    'assinante.aprovado': 'Conta aprovada',
    'assinante.recusado': 'Cadastro recusado',
    // `credenciais_reenviadas` foi partida em duas: quem audita precisa
    // distinguir "so trocou a senha" de "mudou por onde se entra na conta". A
    // chave antiga fica no mapa porque o log e imutavel — as linhas ja gravadas
    // com ela continuam existindo e precisam de nome.
    'assinante.credenciais_reenviadas': 'Acesso reenviado',
    'assinante.senha_reemitida': 'Senha reemitida',
    'assinante.email_alterado': 'E-mail de acesso alterado',
    'assinante.cadastro_editado': 'Cadastro corrigido',
    'assinante.plano_definido': 'Plano definido',
    'assinante.plano_trocado': 'Plano trocado',
    'assinante.troca_recusada': 'Troca de plano recusada',
    'bloco.criado': 'Bloco criado',
    'bloco.renomeado': 'Bloco renomeado',
    'bloco.arquivado': 'Bloco arquivado',
    'unidades.geradas': 'Unidades geradas em massa',
    'unidade.criada': 'Unidade criada',
    'unidade.arquivada': 'Unidade arquivada',
    'armario.criado': 'Lugar de guarda cadastrado',
    'armario.editado': 'Lugar de guarda renomeado',
    'armario.desativado': 'Lugar de guarda desativado',
    'armario.reativado': 'Lugar de guarda reativado',
};
export function describePlatformAction(action) {
    return PLATFORM_ACTION_LABELS[action] ?? action;
}
// ---------------------------------------------------------------------------
// Backoffice: os logs do sistema inteiro
// ---------------------------------------------------------------------------
/**
 * As tres trilhas que o sistema escreve.
 *
 * Elas nascem em lugares diferentes e respondem a perguntas diferentes —
 * "o que a Azko fez nessa conta", "o que o sindico fez no predio dele" e "o que
 * aconteceu com esta encomenda". Continuam tabelas separadas no banco; o que a
 * tela do backoffice faz e le-las juntas, com este campo dizendo de onde cada
 * linha veio.
 */
export const LOG_SOURCES = ['plataforma', 'condominio', 'encomenda'];
export const LOG_SOURCE_LABELS = {
    plataforma: 'Azko',
    condominio: 'Condominio',
    encomenda: 'Encomenda',
};
/**
 * As trilhas que o SINDICO alcanca.
 *
 * A da plataforma fica de fora e nao e esquecimento: ela guarda o que a equipe
 * Azko fez nas contas — incluindo motivo de corte de acesso e nome de quem
 * decidiu —, e isso e conversa entre a Azko e o cliente, nao registro do
 * predio.
 */
export const TENANT_LOG_SOURCES = ['condominio', 'encomenda'];
export const LOG_SOURCE_DESCRIPTIONS = {
    plataforma: 'O que a equipe Azko fez nas contas dos clientes.',
    condominio: 'Morador, porteiro, unidade e plano, dentro de cada condominio.',
    encomenda: 'A vida de cada pacote: registro, aviso, QR e entrega.',
};
/** Rotulos de status. O ingles cru do ENUM nao e para quem le a tela. */
export const DISPATCH_STATUS_LABELS = {
    queued: 'na fila',
    sending: 'enviando',
    sent: 'enviado',
    delivered: 'entregue',
    read: 'lido',
    failed: 'falhou',
};
export const DISPATCH_CHANNEL_LABELS = {
    whatsapp: 'WhatsApp',
    email: 'E-mail',
    sms: 'SMS',
    push: 'Push',
};
/**
 * Nomes humanos para os templates. Mesma ideia do `PLATFORM_ACTION_LABELS`:
 * template sem traducao cai no proprio nome, entao um template novo nunca some
 * da tela por esquecimento.
 */
export const DISPATCH_TEMPLATE_LABELS = {
    'packages.arrived': 'Encomenda chegou',
    'conta.solicitacao_recebida': 'Cadastro recebido',
    'conta.credenciais': 'Credenciais de acesso',
    'conta.acesso_movido': 'Acesso movido de e-mail',
    'conta.recusada': 'Cadastro recusado',
    'conta.recuperacao': 'Recuperacao de senha',
};
export function describeDispatchTemplate(template) {
    return DISPATCH_TEMPLATE_LABELS[template] ?? template;
}
//# sourceMappingURL=billing.js.map